<template>
  <AppView
    padd-bottom
  >
    <template v-slot:top-app-view>
      <transition name="fade" mode="out-in" appear>
        <TopNav
          title="Support tickets"
          title-class="font-title pl-body tw-text-xl tw-tracking-wider tw-leading-6 opacity-54"
        />
      </transition>
    </template>

    <div class="px-body">
      <BaseCard v-if="isSuperAdmin" class="tw-flex tw-flex-no-wrap tw-py-2 tw-px-4 tw-items-center tw-border-t-2 tw-border-app-white-87 tw-transition-slow tw-mb-4">
        <label class="tw-text-sm opacity-78">Select Company and Program</label>
        <div class="tw-flex-grow tw-ml-2">
          <v-select
            placeholder="Select Company"
            :class="[
              { '--loading-data': loadingCompanies },
              'my-select tw-bg-app-deep-blue-11 tw-border-b tw-border-black tw-text-black tw-rounded-t-3 tw-rounded-b-none tw-text-sm tw-flex-shrink-0'
            ]"
            label="company"
            v-model="selectedCompany"
            :options="companies"
            :reduce="option => option.id"
            :clearable="false"
            searchable
            filterable
          />
        </div>
        <div class="tw-flex-grow tw-ml-2">
          <v-select
            placeholder="Select Program"
            :class="[
              { '--loading-data': loadingCompanyPrograms },
              'my-select tw-bg-app-deep-blue-11 tw-border-b tw-border-black tw-text-black tw-rounded-t-3 tw-rounded-b-none tw-text-sm tw-flex-shrink-0'
            ]"
            label="name"
            v-model="selectedCompanyProgram"
            :options="companyPrograms"
            :reduce="option => option.id"
            :clearable="false"
            searchable
            filterable
          />
        </div>
      </BaseCard>
    </div>

    <div class="tw-flex px-body tw-py-4 tw-h-full">
      <div class="tw-w-2/5 tw-h-full">
        <BaseCard class="tw-p-4 tw-rounded-32 tw-border-t-2 tw-border-app-white-87 tw-h-full">
          <div class="tw-flex tw-flex-no-wrap tw-items-center tw-justify-between">
            <div
              @click="activeTab = 'open'"
              :class="[
                activeTab === 'open' ? 'tw-border-t-2 tw-border-l-2 tw-border-r-2 tw-text-app-deep-blue' : 'tw-bg-gray-100 tw-border-b-2 tw-bg-app-black-11 tw-bg-opacity-50',
                'tw-w-1/2 tw-cursor-pointer tw-rounded-t-10 tw-rounded-b-none tw-p-2 tw-border-black',
              ]">
              <h4 class="tw-text-sm tw-font-bold tw-uppercase opacity-54 tw-text-center">
                Open tickets ({{ totalOpenTickets }})
              </h4>
            </div>
            <div
              @click="activeTab = 'resolved'"
              :class="[
                activeTab === 'resolved' ? 'tw-border-t-2 tw-border-l-2 tw-border-r-2 tw-text-app-deep-blue' : 'tw-bg-gray-100 tw-border-b-2 tw-bg-app-black-11 tw-bg-opacity-50',
                'tw-w-1/2 tw-cursor-pointer tw-rounded-t-10 tw-rounded-b-none tw-p-2 tw-border-black',
              ]">
              <h4 class="tw-text-sm tw-font-bold tw-uppercase opacity-54 tw-text-center">
                Resolved tickets ({{ totalResolvedTickets }})
              </h4>
            </div>
          </div>

          <template v-if="!contextIsSet">
            <div class="tw-pt-5 tw-h-full tw-flex tw-flex-col tw-items-center tw-justify-center">
              <p class="tw-text-sm tw-text-center opacity-54">
                Please select a company and program to view support tickets
              </p>
            </div>
          </template>

          <template v-if="activeTabTickets.length === 0 && contextIsSet">
            <div class="tw-pt-5 tw-h-full tw-flex tw-flex-col tw-items-center tw-justify-center">
              <p class="tw-text-sm tw-text-center opacity-54">
                No tickets found
              </p>
            </div>
          </template>

          <template v-if="activeTabTickets.length > 0 && contextIsSet">
            <div class="tw-overflow-y-auto tw-max-h-[500px] tw-h-full" style="max-height: 500px;">
              <div v-for="ticket in activeTabTickets" :key="ticket.id" class="tw-p-4 tw-border-b tw-group hover:tw-bg-gray-200 tw-transition-slow">
                <p class="tw-text-sm tw-font-bold tw-mb-2">
                  {{ ticket.chat_message }}
                </p>
                <div class="tw-inline-flex tw-flex-no-wrap tw-items-center">
                  <UserPhoto
                    :photo="ticket.sender.photo"
                    :user-name="{
                      name: ticket.sender.name,
                    }"
                    photo-size="tw-h-6 tw-w-6"
                  />
                  <div class="tw-ml-2">
                    <p class="tw-text-sm opacity-87">
                      {{ ticket.sender.name }} {{ ticket.sender.middle || ticket.sender.last_name }}
                    </p>
                    <p class="tw-text-sm opacity-54">
                      {{ ticket.tym | toJsDate(true) | moment('ddd Do hh:mm A') }}
                    </p>
                  </div>
                </div>
                <div class="tw-flex tw-justify-end tw-mt-2">
                  <BaseButton
                    text="View Ticket"
                    @click="viewTicket(ticket)"
                    class="group-hover:tw-inline-block tw-hidden tw-ml-auto"
                  />
                </div>
              </div>
            </div>
          </template>
        </BaseCard>
      </div>

      <div class="tw-w-3/5 tw-h-full tw-px-5">
        <div v-if="selectedTicket">
          <SupportTicket
            :ticket="selectedTicket"
            @ticket-resolved="ticketResolved"
          />
        </div>
        <div v-else class="tw-h-full">
          <div class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-full">
            <p class="tw-text-sm tw-text-center opacity-54">
              No ticket selected
            </p>
          </div>
        </div>
      </div>
    </div>
  </AppView>
</template>

<script>
import UserPhoto from '@/components/UserPhoto.vue';
import SupportTicket from '@/components/SupportTicket.vue';

export default {
  name: 'SupportView',
  components: {
    UserPhoto,
    SupportTicket,
  },
  data() {
    return {
      activeTab: 'open',
      openTickets: [],
      resolvedTickets: [],
      selectedTicket: undefined,
      ticketResolution: '',
      ticketResolutionLoading: false,
      companies: [],
      selectedCompany: undefined,
      companyPrograms: [],
      selectedCompanyProgram: undefined,
      loadingCompanies: true,
      loadingCompanyPrograms: false,
    };
  },
  watch: {
    async selectedCompany(val) {
      if (val) {
        this.$store.commit('setSupportContext', val);
        await this.getCompanyPrograms();
      }
    },
    selectedCompanyProgram(val) {
      if (val) {
        const selectedProgram = this.companyPrograms.find((program) => program.id === val);
        this.$store.commit('setSupportProgramContext', val);
        this.$store.commit('setSupportProgramContextType', selectedProgram.type);

        this.getOpenTickets();
        this.getResolvedTickets();
      }
    },
  },
  computed: {
    totalOpenTickets() {
      return this.openTickets.length;
    },
    totalResolvedTickets() {
      return this.resolvedTickets.length;
    },
    contextIsSet() {
      return this.$store.getters.supportContextId && this.$store.getters.supportProgramContextId;
    },
    activeTabTickets() {
      return this.activeTab === 'open' ? this.openTickets : this.resolvedTickets;
    },
    selectedTicketIsResolved() {
      // eslint-disable-next-line eqeqeq
      return this.selectedTicket?.prog_status == 1;
    },
  },
  methods: {
    viewTicket(ticket) {
      this.selectedTicket = ticket;
    },
    async ticketResolved() {
      this.getOpenTickets();
      this.getResolvedTickets();
    },
    async getOpenTickets() {
      const response = await this.$store.dispatch('getOpenTickets');

      if (Array.isArray(response)) {
        this.openTickets = response;
      } else {
        this.$toasted.global.appError({
          errorMessage: 'Error fetching open tickets',
        });
      }
    },

    async getResolvedTickets() {
      const response = await this.$store.dispatch('getResolvedTickets');

      if (Array.isArray(response)) {
        this.resolvedTickets = response;
      } else {
        this.$toasted.global.appError({
          errorMessage: 'Error fetching resolved tickets',
        });
      }
    },

    async getCompanies() {
      if (this.isSuperAdmin) {
        this.loadingCompanies = true;
        const companies = await this.$store.dispatch('getCompanies');

        if (Array.isArray(companies)) {
          this.companies = companies;
          // this.$store.commit('setSupportContext', companies[0].id);
        }

        await this.$nextTick();

        this.loadingCompanies = false;
      }
    },

    async getCompanyPrograms() {
      if (this.isSuperAdmin) {
        this.loadingCompanyPrograms = true;
        await this.$nextTick();

        const companyPrograms = await this.$store.dispatch('getCompanyPrograms', [this.$store.getters.supportContextId]);

        if (Array.isArray(companyPrograms)) {
          this.companyPrograms = companyPrograms;
          // this.$store.commit('setSupportProgramContext', companyPrograms[0].id);
        }

        await this.$nextTick();

        this.loadingCompanyPrograms = false;
      }
    },
  },
  async created() {
    if (this.isSuperAdmin) {
      await this.getCompanies();
    } else {
      this.$store.commit('setSupportContext', this.$store.getters.userCompanyId);
      this.$store.commit('setSupportProgramContext', this.$store.getters.userCompanyProgramId);
      this.$store.commit('setSupportProgramContextType', this.$store.state.CompanyPrograms.ProgramContext.activeProgram?.type);

      this.getOpenTickets();
      this.getResolvedTickets();
    }
  },
};
</script>

<style>

</style>
